/**
 * Normal Trading V1 TESTING — same board UI as live V1, demo data only.
 * Uses /api/normal-v1-test/* — never touches live board or live Master.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import TradingActionBoard from './TradingActionBoard';
import IstClock from './IstClock';
import { fmtRsPerShare } from './trading-rules';
import './NormalTrading.css';
import './NormalTradingV1.css';

const API = '/api/normal-v1-test';

export default function NormalTradingV1Testing() {
  const [status, setStatus] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioId, setScenarioId] = useState('master-demo');
  const [busy, setBusy] = useState(false);
  const [testReport, setTestReport] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const loadBoard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/live-board`, { timeout: 15000 });
      setStatus(res.data);
      if (res.data?.scenarioId) setScenarioId(res.data.scenarioId);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load TEST board');
    }
  }, []);

  const loadScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenarios`, { timeout: 10000 });
      setScenarios(res.data?.scenarios || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadScenarios();
    loadBoard();
    const t = setInterval(loadBoard, 8000);
    return () => clearInterval(t);
  }, [loadBoard, loadScenarios]);

  const lists = status?.actionLists || {};
  const buy = lists.buy || [];
  const hold = lists.hold || [];
  const sell = lists.sell || [];
  const sellActive = sell.filter((s) => !s.soldToday);
  const sellHistory = lists.soldToday || [];
  const stopLossToday = lists.stopLossToday || [];
  const bestPerformersToday = lists.bestPerformersToday || [];

  const master = status?.v1Master || {};
  const profitRs = Number(master.buyProfitRs ?? 0.5);
  const profitLabel = fmtRsPerShare(profitRs);
  const holdMinutes = Number(master.maxHoldMinutes ?? 10);

  const validation = status?.scenarioValidation;
  const totalDay = status?.totalDayPnl ?? 0;

  const loadScenario = async (id) => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const res = await axios.post(`${API}/load-demo`, { id }, { timeout: 15000 });
      setStatus(res.data);
      setScenarioId(res.data?.scenarioId || id);
      const hint = res.data?.scenarioHint ? ` · ${res.data.scenarioHint}` : '';
      setMessage((res.data?.message || `Loaded ${id}`) + hint);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Load demo failed');
    } finally {
      setBusy(false);
    }
  };

  const runAllTests = async () => {
    setBusy(true);
    setTestReport(null);
    setError(null);
    try {
      const res = await axios.post(`${API}/run-all-tests`, {}, { timeout: 60000 });
      setTestReport(res.data);
      setMessage(res.data?.message || 'Tests finished');
      await loadBoard();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Run tests failed');
    } finally {
      setBusy(false);
    }
  };

  const failedScenarios = useMemo(
    () => (testReport?.scenarios?.results || []).filter((r) => !r.pass),
    [testReport]
  );

  return (
    <div className="nt-desktop nt-v1 nt-no-sidebar nt-v1-test-lab">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>Normal Trading V1 — Testing</h1>
            <p className="nt-subtitle">
              Demo lab for tomorrow&apos;s live check · <strong>does not touch live V1 or live Master</strong>
            </p>
            <p className="nt-v1-header-meta nt-desktop-only">
              Scenario <strong>{status?.scenarioName || '—'}</strong>
              {' · '}
              P&amp;L <strong className={totalDay >= 0 ? 'green' : 'red'}>₹{Number(totalDay).toFixed(0)}</strong>
              {' · '}
              <Link to="/NormalTrading/Testing/Master">V1 Master TEST</Link>
              {' · '}
              <Link to="/NormalTrading">← Live V1 (real)</Link>
            </p>
          </div>
          <div className="nt-v1-header-right">
            <IstClock />
            <div className="nt-status-pill running">
              <span className="nt-status-dot" />
              TEST LAB
            </div>
          </div>
        </header>

        <section className="nt-v1-toolbar card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            Scenario
            <select
              value={scenarioId}
              disabled={busy}
              onChange={(e) => loadScenario(e.target.value)}
              style={{ minWidth: 280 }}
            >
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>{s.rule} — {s.name}</option>
              ))}
            </select>
          </label>
          <button type="button" className="nt-v1-btn primary" disabled={busy} onClick={() => loadScenario(scenarioId)}>
            Load demo
          </button>
          <button type="button" className="nt-v1-btn danger" disabled={busy} onClick={() => loadScenario('net-stop-ioc')} title="IOC net −₹0.57 → SELL">
            Test stop → SELL
          </button>
          <button type="button" className="nt-v1-btn danger" disabled={busy} onClick={() => loadScenario('net-stop-sold')} title="After 30s → Stop loss today">
            Test stop → Sold
          </button>
          <button type="button" className="nt-v1-btn" disabled={busy} onClick={() => loadScenario('net-stop-below')}>
            Test stop stay HOLD
          </button>
          <button type="button" className="nt-v1-btn" disabled={busy} onClick={loadBoard}>
            Refresh
          </button>
          <button type="button" className="nt-v1-btn gold" disabled={busy} onClick={runAllTests}>
            {busy ? 'Running…' : 'Run all tests'}
          </button>
          <Link to="/NormalTrading/Testing/Master" className="nt-v1-btn">
            V1 Master TEST
          </Link>
        </section>

        {(status?.scenarioHint || (scenarioId || '').startsWith('net-stop')) && (
          <p className="nt-v1-header-meta" style={{ marginTop: 6 }}>
            <strong>Stop-loss check:</strong>{' '}
            {status?.scenarioHint
              || 'Use the red Test stop buttons · SELL tab for step 1 · Stop loss today tab for step 2'}
            {' · '}
            SELL {sellActive.length} · Stop loss today {stopLossToday.length} · Sold {sellHistory.length}
          </p>
        )}

        {message && <div className="nt-v1-toast">{message}</div>}
        {error && <div className="nt-v1-master-error">{error}</div>}

        {validation && (
          <p className="nt-v1-header-meta" style={{ marginTop: 4 }}>
            Scenario check:{' '}
            <strong className={validation.pass ? 'green' : 'red'}>
              {validation.pass ? 'PASS' : 'FAIL'}
            </strong>
            {(validation.checks || []).map((c, i) => (
              <span key={i} style={{ marginLeft: 8, opacity: c.pass ? 0.85 : 1 }}>
                {c.pass ? '✓' : '✗'} {c.msg}
              </span>
            ))}
          </p>
        )}

        {testReport && (
          <section className="card" style={{ margin: '10px 0', padding: 12, fontSize: 13 }}>
            <strong>{testReport.allPass ? '✅ All passed' : '⚠ Some failed'}</strong>
            <span style={{ marginLeft: 8 }}>{testReport.message}</span>
            <div style={{ marginTop: 8 }}>
              Scenarios: {testReport.scenarios?.passed}/{testReport.scenarios?.total}
              {' · '}
              Rules engine: {testReport.rules?.passed}/{testReport.rules?.total}
            </div>
            {failedScenarios.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                {failedScenarios.map((r) => (
                  <li key={r.id} className="red">
                    {r.name}: {(r.checks || []).filter((c) => !c.pass).map((c) => c.msg).join('; ')}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <TradingActionBoard
          buy={buy}
          hold={hold}
          sell={sell}
          sellActive={sellActive}
          sellHistory={sellHistory}
          bestPerformersToday={bestPerformersToday}
          stopLossToday={stopLossToday}
          sellUrgentCount={sellActive.length}
          sellTodayCount={sellHistory.length}
          activeStep={sellActive.length ? 2 : buy.length ? 1 : 3}
          profitLabel={profitLabel}
          profitRs={profitRs}
          holdPartialMin={master.holdEscapeMinRs ?? 0.1}
          holdPartialMax={master.holdEscapeMaxRs ?? 1000}
          holdMinutes={holdMinutes}
          stockQty={master.maxBuyQty || 50}
          slotsMax={master.slotsMax || 5}
          volatileMode
          quantSlotCount={status?.basket?.slotsUsed ?? 0}
          liveStatus="live"
          priceSource="v1_test_demo"
          strategyCompareMode={false}
          fullyAutomated
          slotsFree={status?.basket?.slotsFree}
          holdSlotCount={hold.length}
        />

        <p className="nt-v1-header-meta" style={{ marginTop: 12, opacity: 0.8 }}>
          How to use for tomorrow: open scenarios → Run all tests → confirm PASS → then trade on{' '}
          <Link to="/NormalTrading">live Normal V1</Link> with{' '}
          <Link to="/NormalTrading/Master">live V1 Master</Link>.
        </p>
      </main>
    </div>
  );
}
